import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppLayout from '../components/AppLayout';
import RichTextEditor from '../components/RichTextEditor';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Save, Plus, Trash2, GripVertical, FileText, 
  Users, DollarSign, Calendar, Shield, Pen, BookOpen,
  Copy, ChevronDown, ChevronUp, Eye, Library, X, PlusCircle, BookmarkPlus,
  ToggleLeft, ToggleRight, Paintbrush, Code
} from 'lucide-react';

// Lazy load the visual editor to avoid bundle size issues
const SectionVisualEditor = lazy(() => import('../components/SectionVisualEditor'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to fix relative image URLs in HTML content
const fixImageUrls = (htmlContent) => {
  if (!htmlContent) return '';
  // Convert relative /api/public/uploads/ or /api/uploads/ paths to absolute URLs
  // Also handle any old preview URLs or legacy domains that might be stored
  return htmlContent
    // Relative paths
    .replace(/src=["']\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Preview domain URLs
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.preview\.emergentagent\.com\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    // Legacy emergent.host domain URLs
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`)
    .replace(/src=["']https:\/\/[^"']*\.emergent\.host\/api\/public\/uploads\//g, `src="${BACKEND_URL}/api/public/uploads/`);
};

// Section type icons and labels
const sectionTypes = {
  cover: { icon: FileText, label: 'Cover Page', color: 'text-purple-400' },
  about: { icon: BookOpen, label: 'About Us', color: 'text-blue-400' },
  scope: { icon: Shield, label: 'Scope of Work', color: 'text-green-400' },
  timeline: { icon: Calendar, label: 'Timeline', color: 'text-yellow-400' },
  pricing: { icon: DollarSign, label: 'Pricing', color: 'text-[#A2FF00]' },
  terms: { icon: FileText, label: 'Terms & Conditions', color: 'text-orange-400' },
  signature: { icon: Pen, label: 'Signature Block', color: 'text-pink-400' },
  custom: { icon: FileText, label: 'Custom Section', color: 'text-gray-400' }
};

// Default section library
const defaultSectionLibrary = [
  {
    id: 'lib_cover',
    section_type: 'cover',
    title: 'Cover Page',
    content: `<div class="cover-page">
<h1>Proposal</h1>
<h2>Prepared for {company_name}</h2>
<p>Date: {proposal_date}</p>
<p>Valid until: {proposal_expiry}</p>
<p>Prepared for: {main_contact_name}</p>
</div>`,
    is_required: true
  },
  {
    id: 'lib_about',
    section_type: 'about',
    title: 'About QSECDEF',
    content: `<h2>About Quantum Security & Defence</h2>
<p>QSECDEF is the premier platform connecting quantum, cyber security, and AI security companies with industry leaders through elite B2B events and comprehensive media production.</p>
<p>Our mission is to elevate your brand visibility and establish thought leadership in the rapidly evolving security landscape.</p>`,
    is_required: false
  },
  {
    id: 'lib_scope_membership',
    section_type: 'scope',
    title: 'Membership Benefits',
    content: `<h2>Your Membership Includes</h2>
<h3>🎯 Elite Events</h3>
<ul>
<li>Speaking opportunity at quarterly events</li>
<li>VIP networking sessions</li>
<li>Priority event scheduling</li>
</ul>
<h3>🎬 Media Production</h3>
<ul>
<li>Professional video production</li>
<li>Short-form content creation</li>
<li>Social media assets</li>
</ul>
<h3>📤 Distribution</h3>
<ul>
<li>Multi-platform content distribution</li>
<li>YouTube channel feature</li>
<li>LinkedIn amplification</li>
</ul>`,
    is_required: false
  },
  {
    id: 'lib_scope_sponsorship',
    section_type: 'scope',
    title: 'Sponsorship Benefits',
    content: `<h2>Sponsorship Package</h2>
<h3>🎯 Event Presence</h3>
<ul>
<li>Logo placement on all event materials</li>
<li>Dedicated sponsor spotlight segment</li>
<li>Branded booth/virtual room</li>
</ul>
<h3>📣 Marketing Benefits</h3>
<ul>
<li>Pre-event promotional features</li>
<li>Social media mentions</li>
<li>Email newsletter inclusion</li>
</ul>`,
    is_required: false
  },
  {
    id: 'lib_scope_consulting',
    section_type: 'scope',
    title: 'Consulting Services',
    content: `<h2>Consulting Engagement</h2>
<h3>📋 Assessment Phase</h3>
<ul>
<li>Initial discovery and requirements gathering</li>
<li>Current state analysis</li>
<li>Gap identification</li>
</ul>
<h3>🎯 Implementation Phase</h3>
<ul>
<li>Strategy development</li>
<li>Solution implementation</li>
<li>Knowledge transfer</li>
</ul>
<h3>📊 Review Phase</h3>
<ul>
<li>Progress review meetings</li>
<li>Final deliverables</li>
<li>Recommendations report</li>
</ul>`,
    is_required: false
  },
  {
    id: 'lib_timeline_standard',
    section_type: 'timeline',
    title: 'Project Timeline',
    content: `<h2>Timeline & Milestones</h2>
<table>
<tr><th>Phase</th><th>Timeline</th><th>Activities</th></tr>
<tr><td>Kickoff</td><td>Week 1</td><td>Contract signing, onboarding</td></tr>
<tr><td>Planning</td><td>Weeks 2-3</td><td>Requirements, scheduling</td></tr>
<tr><td>Execution</td><td>Weeks 4-8</td><td>Core delivery</td></tr>
<tr><td>Review</td><td>Week 9</td><td>Final review and handover</td></tr>
</table>`,
    is_required: false
  },
  {
    id: 'lib_timeline_event',
    section_type: 'timeline',
    title: 'Event Timeline',
    content: `<h2>Event Timeline</h2>
<table>
<tr><th>Phase</th><th>Timeline</th><th>Activities</th></tr>
<tr><td>Pre-Event</td><td>4 weeks before</td><td>Marketing launch, speaker prep</td></tr>
<tr><td>Event Week</td><td>Event date</td><td>Live event, recording</td></tr>
<tr><td>Post-Event</td><td>2 weeks after</td><td>Content editing, distribution</td></tr>
</table>`,
    is_required: false
  },
  {
    id: 'lib_pricing',
    section_type: 'pricing',
    title: 'Investment',
    content: `<h2>Your Investment</h2>
<p>Select your preferred options below. Pricing will be calculated based on your selection.</p>`,
    is_required: true
  },
  {
    id: 'lib_terms_standard',
    section_type: 'terms',
    title: 'Terms & Conditions',
    content: `<h2>Terms & Conditions</h2>
<h3>1. Payment Terms</h3>
<p>Payment is due within 14 days of contract signing.</p>
<h3>2. Cancellation Policy</h3>
<p>Cancellation requires 30 days written notice.</p>
<h3>3. Confidentiality</h3>
<p>Both parties agree to maintain confidentiality of proprietary information.</p>
<h3>4. Limitation of Liability</h3>
<p>Liability is limited to the total contract value.</p>`,
    is_required: false
  },
  {
    id: 'lib_signature',
    section_type: 'signature',
    title: 'Agreement',
    content: `<h2>Accept This Proposal</h2>
<p>By signing below, you confirm that you have read and agree to the terms outlined in this proposal.</p>
<p><strong>Client:</strong> {company_name}</p>
<p><strong>Authorized Signatory:</strong> {main_contact_name}</p>
<p><strong>Title:</strong> {main_contact_title}</p>`,
    is_required: true
  }
];

// Sortable Section Item
const SortableSection = ({ section, index, isActive, onSelect, onDelete, onToggleRequired, isDark }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.section_id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  const typeConfig = sectionTypes[section.section_type] || sectionTypes.custom;
  const Icon = typeConfig.icon;

  const handleDelete = (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Use setTimeout to escape any drag context
    setTimeout(() => {
      onDelete(section.section_id);
    }, 0);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'} border rounded-sm p-3 transition-colors ${
        isActive 
          ? 'border-[#A2FF00]' 
          : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className={`cursor-grab ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-600'} p-1 touch-none`}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        
        <button
          type="button"
          onClick={() => onSelect(section.section_id)}
          className="flex-1 flex items-center gap-2 text-left min-w-0"
        >
          <Icon className={`w-4 h-4 flex-shrink-0 ${typeConfig.color}`} />
          <span className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm font-medium truncate`}>{section.title}</span>
        </button>
        
        <div className="flex items-center gap-1 flex-shrink-0">
          {section.is_required && (
            <span className={`text-xs ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} px-1`}>Required</span>
          )}
          <button
            type="button"
            onClick={handleDelete}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
            title="Remove section"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const isNew = templateId === 'new';
  const isAdmin = user?.role === 'admin';
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [sectionLibrary, setSectionLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showSaveToLibraryModal, setShowSaveToLibraryModal] = useState(false);
  const [showCreateSectionModal, setShowCreateSectionModal] = useState(false);
  const [newLibrarySectionName, setNewLibrarySectionName] = useState('');
  const [newSectionData, setNewSectionData] = useState({
    title: '',
    content: '',
    visual_content: '',
    editor_mode: 'richtext',
    section_type: 'custom'
  });
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    fetchProducts();
    fetchLibrarySections();
    if (isNew) {
      // Initialize new template
      setTemplate({
        name: 'New Proposal Template',
        description: '',
        sections: [],
        branding: {
          logo_url: '/uploads/qsecdef-logo.png',
          primary_color: '#A2FF00',
          secondary_color: '#0A0A0A',
          font_family: 'Unbounded, sans-serif'
        },
        default_products: [],
        default_validity_days: 30,
        is_default: false
      });
    } else {
      fetchTemplate();
    }
  }, [templateId, isNew]);

  const fetchLibrarySections = async () => {
    setLibraryLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections`, { credentials: 'include' });
      if (response.ok) {
        const sections = await response.json();
        // If no sections in DB, seed with defaults
        if (sections.length === 0) {
          // Use default sections as fallback
          setSectionLibrary(defaultSectionLibrary);
        } else {
          setSectionLibrary(sections);
        }
      } else {
        // Fallback to defaults if API fails
        setSectionLibrary(defaultSectionLibrary);
      }
    } catch (error) {
      console.error('Failed to fetch library sections:', error);
      setSectionLibrary(defaultSectionLibrary);
    } finally {
      setLibraryLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, { credentials: 'include' });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Fix image URLs in all sections
        if (data.sections) {
          data.sections = data.sections.map(section => ({
            ...section,
            content: fixImageUrls(section.content)
          }));
        }
        setTemplate(data);
        if (data.sections?.length > 0) {
          setActiveSection(data.sections[0].section_id);
        }
      } else {
        alert('Template not found');
        navigate('/proposals/templates');
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!template.name.trim()) {
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      const url = isNew 
        ? `${BACKEND_URL}/api/proposal-templates`
        : `${BACKEND_URL}/api/proposal-templates/${templateId}`;
      
      const method = isNew ? 'POST' : 'PATCH';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: template.name,
          description: template.description,
          sections: template.sections,
          branding: template.branding,
          default_products: template.default_products || [],
          default_validity_days: template.default_validity_days,
          is_default: template.is_default
        })
      });

      if (response.ok) {
        const savedTemplate = await response.json();
        if (isNew) {
          navigate(`/proposals/templates/${savedTemplate.template_id}/edit`);
        }
        alert('Template saved successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const addSectionFromLibrary = (libSection) => {
    const newSection = {
      section_id: `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      section_type: libSection.section_type,
      title: libSection.title,
      content: libSection.content,
      is_required: libSection.is_required || false,
      sort_order: template.sections.length
    };

    setTemplate({
      ...template,
      sections: [...template.sections, newSection]
    });
    setActiveSection(newSection.section_id);
    setShowLibrary(false);
  };

  // Save current section to library
  const saveToLibrary = async () => {
    const currentSection = template.sections?.find(s => s.section_id === activeSection);
    if (!currentSection) return;

    setSavingToLibrary(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: currentSection.title,
          content: currentSection.content,
          section_type: currentSection.section_type
        })
      });

      if (response.ok) {
        alert('Section saved to library!');
        fetchLibrarySections(); // Refresh library
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save to library');
      }
    } catch (error) {
      console.error('Save to library error:', error);
      alert('Failed to save to library');
    } finally {
      setSavingToLibrary(false);
    }
  };

  // Create new section in library
  const createLibrarySection = async () => {
    if (!newSectionData.title.trim()) {
      alert('Please enter a section title');
      return;
    }

    setSavingToLibrary(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newSectionData.title,
          content: newSectionData.editor_mode === 'visual' ? '' : (newSectionData.content || '<p>Enter content here...</p>'),
          visual_content: newSectionData.editor_mode === 'visual' ? newSectionData.visual_content : null,
          editor_mode: newSectionData.editor_mode,
          section_type: newSectionData.section_type
        })
      });

      if (response.ok) {
        alert('Section created in library!');
        setShowCreateSectionModal(false);
        setNewSectionData({ title: '', content: '', visual_content: '', editor_mode: 'richtext', section_type: 'custom' });
        fetchLibrarySections(); // Refresh library
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create section');
      }
    } catch (error) {
      console.error('Create section error:', error);
      alert('Failed to create section');
    } finally {
      setSavingToLibrary(false);
    }
  };

  // Delete section from library (admin only)
  const deleteLibrarySection = async (sectionId) => {
    if (!isAdmin) {
      alert('Only admins can delete library sections');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this section from the library? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/library-sections/${sectionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchLibrarySections(); // Refresh library
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete section');
      }
    } catch (error) {
      console.error('Delete section error:', error);
      alert('Failed to delete section');
    }
  };

  const addCustomSection = () => {
    const newSection = {
      section_id: `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      section_type: 'custom',
      title: 'New Section',
      content: '<p>Enter your content here...</p>',
      is_required: false,
      sort_order: template.sections.length
    };

    setTemplate({
      ...template,
      sections: [...template.sections, newSection]
    });
    setActiveSection(newSection.section_id);
  };

  const updateSection = (sectionId, updates) => {
    setTemplate({
      ...template,
      sections: template.sections.map(s =>
        s.section_id === sectionId ? { ...s, ...updates } : s
      )
    });
  };

  const deleteSection = (sectionId) => {
    if (!window.confirm('Remove this section from the template?')) return;
    
    const newSections = template.sections.filter(s => s.section_id !== sectionId);
    setTemplate({ ...template, sections: newSections });
    
    if (activeSection === sectionId) {
      setActiveSection(newSections.length > 0 ? newSections[0].section_id : null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = template.sections.findIndex(s => s.section_id === active.id);
      const newIndex = template.sections.findIndex(s => s.section_id === over.id);
      
      const newSections = arrayMove(template.sections, oldIndex, newIndex).map((s, i) => ({
        ...s,
        sort_order: i
      }));
      
      setTemplate({ ...template, sections: newSections });
    }
  };

  if (loading) {
    return (
      <AppLayout title="Template Editor">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!template) return null;

  const activeContent = template.sections?.find(s => s.section_id === activeSection);

  return (
    <AppLayout title="Template Editor">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/proposals/templates')}
              className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <input
                type="text"
                value={template.name}
                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} bg-transparent border-b border-transparent hover:border-white/30 focus:border-[#A2FF00] outline-none`}
                style={{ fontFamily: 'Unbounded, sans-serif' }}
                placeholder="Template Name"
              />
              <input
                type="text"
                value={template.description || ''}
                onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                className={`block ${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm bg-transparent border-b border-transparent hover:border-white/30 focus:border-[#A2FF00] outline-none w-full mt-1`}
                placeholder="Template description (optional)"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && template.template_id && (
              <button
                onClick={() => window.open(`/proposals/templates/${template.template_id}/preview`, '_blank')}
                className={`flex items-center gap-2 px-3 py-2 ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} rounded-sm transition-colors`}
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            )}
            <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <input
                type="checkbox"
                checked={template.is_default}
                onChange={(e) => setTemplate({ ...template, is_default: e.target.checked })}
                className="w-4 h-4"
              />
              Set as default
            </label>
            <button
              onClick={saveTemplate}
              disabled={saving}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors disabled:opacity-50`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        {/* Editor Layout */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sections Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>SECTIONS</h3>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{template.sections.length} items</span>
              </div>
              
              {template.sections.length === 0 ? (
                <div className={`text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'} text-sm`}>
                  No sections yet. Add from library or create custom.
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={template.sections.map(s => s.section_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {template.sections.map((section, index) => (
                        <SortableSection
                          key={section.section_id}
                          section={section}
                          index={index}
                          isActive={activeSection === section.section_id}
                          onSelect={setActiveSection}
                          onDelete={deleteSection}
                          isDark={isDark}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              
              <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} space-y-2`}>
                <button
                  onClick={() => setShowLibrary(true)}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] hover:bg-[#A2FF00]/20' : 'bg-green-50 text-green-600 hover:bg-green-100'} rounded-sm transition-colors text-sm`}
                >
                  <Library className="w-4 h-4" />
                  Add from Library
                </button>
                <button
                  onClick={addCustomSection}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2 ${isDark ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-sm transition-colors text-sm`}
                >
                  <Plus className="w-4 h-4" />
                  Add Custom Section
                </button>
              </div>
            </div>

            {/* Template Settings */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-3`}>SETTINGS</h3>
              <div className="space-y-3">
                <div>
                  <label className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} block mb-1`}>Validity (days)</label>
                  <input
                    type="number"
                    value={template.default_validity_days}
                    onChange={(e) => setTemplate({ ...template, default_validity_days: parseInt(e.target.value) || 30 })}
                    min="1"
                    max="365"
                    className={`w-full ${isDark ? 'bg-[#0A0A0A] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 text-sm focus:border-[#A2FF00] outline-none`}
                  />
                </div>
              </div>
            </div>

            {/* Default Products */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>DEFAULT PRODUCTS</h3>
                <button
                  onClick={() => setShowProductModal(true)}
                  className={`text-xs ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-500 hover:text-green-600'} transition-colors`}
                >
                  + Add
                </button>
              </div>
              
              {(!template.default_products || template.default_products.length === 0) ? (
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} text-center py-2`}>
                  No default products. Add products to pre-populate proposals.
                </p>
              ) : (
                <div className="space-y-2">
                  {template.default_products.map((dp, idx) => {
                    const product = products.find(p => p.product_id === dp.product_id);
                    return (
                      <div key={idx} className={`flex items-center justify-between ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} rounded p-2`}>
                        <div>
                          <span className={`${isDark ? 'text-white' : 'text-gray-900'} text-sm`}>{dp.product_name}</span>
                          {dp.include_variants?.length > 0 && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({dp.include_variants.length} variant{dp.include_variants.length > 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            setTemplate({
                              ...template,
                              default_products: template.default_products.filter((_, i) => i !== idx)
                            });
                          }}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Merge Tags Reference */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-3`}>MERGE TAGS</h3>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mb-3`}>
                Use these placeholders in your content. They&apos;ll be replaced with actual data when creating proposals.
              </p>
              <div className={`text-xs space-y-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Company</span>
                  <div className={`mt-1 ${isDark ? 'bg-black/30' : 'bg-gray-50'} rounded p-2 font-mono text-[10px] space-y-1`}>
                    <div>{'{company_name}'}</div>
                    <div>{'{company_address}'}</div>
                    <div>{'{city_town}'}</div>
                    <div>{'{country}'}</div>
                    <div>{'{website_url}'}</div>
                    <div>{'{customer_bio}'}</div>
                  </div>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Main Contact</span>
                  <div className={`mt-1 ${isDark ? 'bg-black/30' : 'bg-gray-50'} rounded p-2 font-mono text-[10px] space-y-1`}>
                    <div>{'{main_contact_name}'}</div>
                    <div>{'{main_contact_first_name}'}</div>
                    <div>{'{main_contact_last_name}'}</div>
                    <div>{'{main_contact_email}'}</div>
                    <div>{'{main_contact_title}'}</div>
                  </div>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Other Contacts</span>
                  <div className={`mt-1 ${isDark ? 'bg-black/30' : 'bg-gray-50'} rounded p-2 font-mono text-[10px] space-y-1`}>
                    <div>{'{billing_contact_name}'}</div>
                    <div>{'{billing_contact_email}'}</div>
                    <div>{'{cmo_name}'}</div>
                    <div>{'{ceo_name}'}</div>
                  </div>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Speakers</span>
                  <div className={`mt-1 ${isDark ? 'bg-black/30' : 'bg-gray-50'} rounded p-2 font-mono text-[10px] space-y-1`}>
                    <div>{'{speaker_1_name}'}</div>
                    <div>{'{speaker_1_title}'}</div>
                    <div>{'{speaker_1_email}'}</div>
                    <div>{'{speaker_2_name}'}</div>
                    <div>{'{speaker_2_title}'}</div>
                  </div>
                </div>
                <div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-700'}`}>Dates & Billing</span>
                  <div className={`mt-1 ${isDark ? 'bg-black/30' : 'bg-gray-50'} rounded p-2 font-mono text-[10px] space-y-1`}>
                    <div>{'{proposal_date}'}</div>
                    <div>{'{proposal_expiry}'}</div>
                    <div>{'{event_date}'}</div>
                    <div>{'{contract_date}'}</div>
                    <div>{'{billing_amount}'}</div>
                    <div>{'{billing_type}'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Editor */}
          <div className="lg:col-span-3">
            {activeContent ? (
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm`}>
                <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-4">
                    <input
                      type="text"
                      value={activeContent.title}
                      onChange={(e) => updateSection(activeContent.section_id, { title: e.target.value })}
                      className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} bg-transparent border-b border-transparent hover:border-white/30 focus:border-[#A2FF00] outline-none flex-1`}
                      placeholder="Section Title"
                    />
                    <span className={`text-xs px-2 py-1 rounded ${sectionTypes[activeContent.section_type]?.color || 'text-gray-400'} bg-white/5`}>
                      {sectionTypes[activeContent.section_type]?.label || 'Custom'}
                    </span>
                    <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <input
                        type="checkbox"
                        checked={activeContent.is_required}
                        onChange={(e) => updateSection(activeContent.section_id, { is_required: e.target.checked })}
                        className="w-4 h-4"
                      />
                      Required
                    </label>
                    <button
                      onClick={saveToLibrary}
                      disabled={savingToLibrary}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00] hover:bg-[#A2FF00]/20' : 'bg-green-50 text-green-600 hover:bg-green-100'} rounded-sm transition-colors disabled:opacity-50`}
                      title="Save this section to the global library"
                    >
                      <BookmarkPlus className="w-4 h-4" />
                      {savingToLibrary ? 'Saving...' : 'Save to Library'}
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {activeContent.section_type === 'pricing' ? (
                    <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-6 text-center`}>
                      <DollarSign className={`w-12 h-12 ${isDark ? 'text-[#A2FF00]' : 'text-green-500'} mx-auto mb-3`} />
                      <h4 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium mb-2`}>Pricing Section</h4>
                      <p className={`${isDark ? 'text-gray-500' : 'text-gray-600'} text-sm`}>
                        Pricing is dynamically generated from selected products when creating a proposal.
                      </p>
                    </div>
                  ) : activeContent.section_type === 'signature' ? (
                    <div className="space-y-4">
                      <RichTextEditor
                        key={activeContent.section_id}
                        editorKey={activeContent.section_id}
                        content={activeContent.content || ''}
                        onChange={(html) => updateSection(activeContent.section_id, { content: html })}
                        placeholder="Enter signature block content..."
                      />
                      <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-4`}>
                        <p className={`${isDark ? 'text-gray-500' : 'text-gray-600'} text-sm`}>
                          💡 The signature capture area will be automatically added below this content.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Editor Mode Toggle */}
                      <div className="flex items-center justify-end gap-2">
                        <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Editor:</span>
                        <div className={`flex items-center rounded-lg border ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-gray-300 bg-gray-100'}`}>
                          <button
                            onClick={() => updateSection(activeContent.section_id, { editor_mode: 'richtext', visual_content: null })}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-l-lg transition-colors ${
                              activeContent.editor_mode !== 'visual'
                                ? isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'
                                : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <Code className="h-3.5 w-3.5" />
                            Rich Text
                          </button>
                          <button
                            onClick={() => updateSection(activeContent.section_id, { editor_mode: 'visual' })}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-r-lg transition-colors ${
                              activeContent.editor_mode === 'visual'
                                ? isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'
                                : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <Paintbrush className="h-3.5 w-3.5" />
                            Visual
                          </button>
                        </div>
                      </div>

                      {/* Editor Content */}
                      {activeContent.editor_mode === 'visual' ? (
                        <Suspense fallback={
                          <div className={`h-[400px] flex items-center justify-center ${isDark ? 'bg-zinc-900' : 'bg-gray-100'} rounded-lg`}>
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00] mx-auto mb-2"></div>
                              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Loading visual editor...</p>
                            </div>
                          </div>
                        }>
                          <SectionVisualEditor
                            content={activeContent.visual_content || ''}
                            onChange={(content) => updateSection(activeContent.section_id, { visual_content: content })}
                          />
                        </Suspense>
                      ) : (
                        <RichTextEditor
                          key={activeContent.section_id}
                          editorKey={activeContent.section_id}
                          content={activeContent.content || ''}
                          onChange={(html) => updateSection(activeContent.section_id, { content: html })}
                          placeholder="Enter section content..."
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-gray-50 border-gray-200'} border rounded-sm p-12 text-center`}>
                <FileText className={`w-16 h-16 ${isDark ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} />
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>No Section Selected</h3>
                <p className={`${isDark ? 'text-gray-500' : 'text-gray-600'} mb-4`}>
                  Add sections from the library or create custom sections to build your template.
                </p>
                <button
                  onClick={() => setShowLibrary(true)}
                  className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black font-bold rounded-sm hover:opacity-90 transition-colors`}
                >
                  Browse Section Library
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Library Modal */}
      {showLibrary && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col`}>
            <div className={`p-6 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
              <div>
                <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Section Library
                </h2>
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm mt-1`}>Choose a section to add to your template</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowLibrary(false);
                    setShowCreateSectionModal(true);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors text-sm`}
                >
                  <PlusCircle className="w-4 h-4" />
                  Create New Section
                </button>
                <button
                  onClick={() => setShowLibrary(false)}
                  className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {libraryLoading ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Loading sections...
                </div>
              ) : sectionLibrary.length === 0 ? (
                <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="mb-4">No sections in library yet.</p>
                  <button
                    onClick={() => {
                      setShowLibrary(false);
                      setShowCreateSectionModal(true);
                    }}
                    className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00]' : 'bg-green-500'} text-black font-bold rounded-sm hover:opacity-90 transition-colors`}
                  >
                    Create First Section
                  </button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {sectionLibrary.map((libSection) => {
                    const typeConfig = sectionTypes[libSection.section_type] || sectionTypes.custom;
                    const Icon = typeConfig.icon;
                    const sectionKey = libSection.section_id || libSection.id;
                    
                    return (
                      <div
                        key={sectionKey}
                        className={`${isDark ? 'bg-[#111] border-white/10 hover:border-[#A2FF00]' : 'bg-gray-50 border-gray-200 hover:border-green-500'} border rounded-sm p-4 transition-colors group relative`}
                      >
                        <button
                          onClick={() => addSectionFromLibrary(libSection)}
                          className="w-full text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded ${typeConfig.color} ${isDark ? 'bg-white/5' : 'bg-gray-200'}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`${isDark ? 'text-white group-hover:text-[#A2FF00]' : 'text-gray-900 group-hover:text-green-600'} font-medium transition-colors truncate`}>
                                {libSection.title}
                              </h4>
                              <p className={`${isDark ? 'text-gray-500' : 'text-gray-400'} text-xs mt-1`}>{typeConfig.label}</p>
                              {libSection.created_by_name && (
                                <p className={`${isDark ? 'text-gray-600' : 'text-gray-400'} text-xs mt-1`}>
                                  by {libSection.created_by_name}
                                </p>
                              )}
                              {libSection.is_required && (
                                <span className={`inline-block mt-2 text-xs ${isDark ? 'text-[#A2FF00] bg-[#A2FF00]/10' : 'text-green-600 bg-green-100'} px-2 py-0.5 rounded`}>
                                  Recommended
                                </span>
                              )}
                            </div>
                            <Plus className={`w-5 h-5 ${isDark ? 'text-gray-600 group-hover:text-[#A2FF00]' : 'text-gray-400 group-hover:text-green-600'} transition-colors flex-shrink-0`} />
                          </div>
                        </button>
                        
                        {/* Admin delete button */}
                        {isAdmin && libSection.section_id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteLibrarySection(libSection.section_id);
                            }}
                            className="absolute top-2 right-2 p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete from library (Admin)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create New Section Modal */}
      {showCreateSectionModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Create New Library Section
              </h3>
              <button
                onClick={() => {
                  setShowCreateSectionModal(false);
                  setNewSectionData({ title: '', content: '', visual_content: '', editor_mode: 'richtext', section_type: 'custom' });
                }}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} p-1`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Section Title *
                </label>
                <input
                  type="text"
                  value={newSectionData.title}
                  onChange={(e) => setNewSectionData({ ...newSectionData, title: e.target.value })}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                  placeholder="e.g., Custom Terms & Conditions"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Section Type
                </label>
                <select
                  value={newSectionData.section_type}
                  onChange={(e) => setNewSectionData({ ...newSectionData, section_type: e.target.value })}
                  className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border rounded-sm px-3 py-2 focus:border-[#A2FF00] outline-none`}
                >
                  {Object.entries(sectionTypes).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              {/* Editor Mode Toggle */}
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  Editor Mode
                </label>
                <div className={`flex items-center rounded-lg border ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-gray-300 bg-gray-100'}`}>
                  <button
                    onClick={() => setNewSectionData({ ...newSectionData, editor_mode: 'richtext' })}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-l-lg transition-colors flex-1 justify-center ${
                      newSectionData.editor_mode !== 'visual'
                        ? isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'
                        : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Code className="h-4 w-4" />
                    Rich Text
                  </button>
                  <button
                    onClick={() => setNewSectionData({ ...newSectionData, editor_mode: 'visual' })}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-r-lg transition-colors flex-1 justify-center ${
                      newSectionData.editor_mode === 'visual'
                        ? isDark ? 'bg-[#A2FF00] text-black' : 'bg-green-500 text-white'
                        : isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Paintbrush className="h-4 w-4" />
                    Visual
                  </button>
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>
                  Content
                </label>
                {newSectionData.editor_mode === 'visual' ? (
                  <Suspense fallback={
                    <div className={`h-[300px] flex items-center justify-center ${isDark ? 'bg-zinc-900' : 'bg-gray-100'} rounded-lg border ${isDark ? 'border-white/20' : 'border-gray-300'}`}>
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A2FF00] mx-auto mb-2"></div>
                        <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>Loading visual editor...</p>
                      </div>
                    </div>
                  }>
                    <SectionVisualEditor
                      content={newSectionData.visual_content || ''}
                      onChange={(content) => setNewSectionData({ ...newSectionData, visual_content: content })}
                    />
                  </Suspense>
                ) : (
                  <div className={`${isDark ? 'bg-[#111] border-white/20' : 'bg-gray-50 border-gray-300'} border rounded-sm min-h-[200px]`}>
                    <RichTextEditor
                      key="new-section-editor"
                      content={newSectionData.content}
                      onChange={(html) => setNewSectionData({ ...newSectionData, content: html })}
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'} flex justify-end gap-3`}>
              <button
                onClick={() => {
                  setShowCreateSectionModal(false);
                  setNewSectionData({ title: '', content: '', visual_content: '', editor_mode: 'richtext', section_type: 'custom' });
                }}
                className={`px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} rounded-sm transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={createLibrarySection}
                disabled={savingToLibrary || !newSectionData.title.trim()}
                className={`px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors disabled:opacity-50`}
              >
                {savingToLibrary ? 'Creating...' : 'Create Section'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showProductModal && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Add Default Product
              </h3>
              <button
                onClick={() => setShowProductModal(false)}
                className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} p-1`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                Select products to automatically include when creating proposals from this template.
              </p>
              
              {products.filter(p => p.is_active).length === 0 ? (
                <p className={`text-center ${isDark ? 'text-gray-500' : 'text-gray-400'} py-8`}>No active products available</p>
              ) : (
                <div className="space-y-3">
                  {products.filter(p => p.is_active).map((product) => {
                    const isAdded = template.default_products?.some(dp => dp.product_id === product.product_id);
                    
                    return (
                      <div
                        key={product.product_id}
                        className={`border rounded-sm p-3 transition-colors ${
                          isAdded 
                            ? isDark ? 'border-[#A2FF00]/50 bg-[#A2FF00]/5' : 'border-green-500/50 bg-green-50'
                            : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{product.name}</h4>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                              £{product.price_gbp.toLocaleString()} - {product.billing_frequency}
                            </p>
                            {product.variants?.length > 0 && (
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                {product.variants.length} pricing variant{product.variants.length > 1 ? 's' : ''} available
                              </p>
                            )}
                          </div>
                          
                          {isAdded ? (
                            <span className={`text-xs ${isDark ? 'text-[#A2FF00] bg-[#A2FF00]/10' : 'text-green-600 bg-green-100'} px-2 py-1 rounded`}>
                              Added
                            </span>
                          ) : (
                            <button
                              onClick={() => {
                                const newDefaultProduct = {
                                  product_id: product.product_id,
                                  product_name: product.name,
                                  include_variants: product.variants?.map(v => v.variant_id) || []
                                };
                                setTemplate({
                                  ...template,
                                  default_products: [...(template.default_products || []), newDefaultProduct]
                                });
                              }}
                              className={`px-3 py-1 text-sm ${isDark ? 'bg-white/10 text-white hover:bg-[#A2FF00]' : 'bg-gray-100 text-gray-700 hover:bg-green-500'} rounded hover:text-black transition-colors`}
                            >
                              Add
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowProductModal(false)}
                className={`w-full px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black font-bold rounded-sm transition-colors`}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default TemplateEditor;
